// Single-criterion drilldown drawer: pass-rate trend + top failing
// agents + scored calls. Click payloads carry criteria_id +
// scorecard_config_sid directly (TREN-308).

import React, { useContext, useMemo } from 'react'
import { isEmpty, isNil } from 'lodash'
import { useSelector } from 'react-redux'

import { EChart } from '@/components/charts/EChart'
import { Card } from '@/components/cards/Card'
import { Button } from '@/components/buttons/Button'
import { Pill } from '@/components/pills/Pill'
import { apiService } from '@/api'
import { CHART } from '@/components/charts/chartPalette'
import { EntityClickContext } from '../helpers/dashboardContexts'
import { buildDashboardParams, useDashboardOrgId } from '../helpers/dashboardParams'
import { ScoredCallsBrowser } from './ScoredCallsBrowser'
import {
  EmptyHint,
  EntityDrawerShell,
  buildDrawerScopeFilters,
  getCriterionLabel,
  useDrawerFetches,
} from './entityDrawer.helpers'
import { ChartCard } from '../components/ChartCard'
import { escapeHtml } from '../helpers/format'

const normName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()

export const CriterionDetailDrawer = ({ criterion, onClose }) => {
  const clickEntity = useContext(EntityClickContext)
  const filters = useSelector((state) => state.qaCopilot.filters)
  const orgId = useDashboardOrgId()

  const scorecardSid = criterion?.scorecard_config_sid || null
  // criteria_id is the scid the trend endpoint keys rows by.
  const criteriaScid = criterion?.criteria_id || criterion?.id || null
  const criterionName = criterion?.name || criterion?.criteria_name
  // Transient agent narrowing (e.g. Failure Streak clicks): scopes every
  // fetch without mutating global filters, so closing the drawer clears it.
  const agentContext = criterion?.agentContext || null

  const trendParams =
    scorecardSid && orgId ? buildDashboardParams(filters, orgId, agentContext).toString() : null
  const trendUrl =
    trendParams &&
    `${apiService.scorecard}/scoring/scores/clickhouse/criteria_trends/by_sid/${scorecardSid}?${trendParams}`
  const {
    data: [trend],
    loading: trendLoading,
    error: trendError,
  } = useDrawerFetches(trendUrl)

  // "Top Agents" is populated from `top_infractors` in the click payload
  // (FlaggedCriteria carries them). Payloads from Scorecard Performance
  // don't (the overview bundle has none), so fetch them for this
  // criterion from the by_criteria feed rather than showing an empty
  // block. Skip the fetch when the payload provided them at all (even an
  // explicit [] means "source already resolved none"), when we can't
  // scope it, or when narrowed to a single agent (block hidden).
  const needsInfractorFetch =
    isNil(criterion?.top_infractors) && !!criteriaScid && !!scorecardSid && !!orgId && !agentContext
  const byCriteriaUrl = needsInfractorFetch
    ? `${apiService.scorecard}/scoring/scores/clickhouse/aggregate/callCount/by_criteria?${buildDashboardParams(
        { ...filters, scorecards: [{ value: scorecardSid }] },
        orgId
      ).toString()}`
    : null
  const {
    data: [byCriteria],
  } = useDrawerFetches(byCriteriaUrl)
  const topInfractors = useMemo(() => {
    if (!isNil(criterion?.top_infractors)) {
      return criterion.top_infractors
    }
    const match = (byCriteria || []).find((row) => row.criteria_id === criteriaScid)
    return match?.top_infractors || []
  }, [criterion, byCriteria, criteriaScid])

  const trendRows = useMemo(() => {
    // Prefer criteria_id (scid) for the filter; fall back to name match.
    const target = normName(criterionName)
    return (trend?.data || [])
      .filter((row) => {
        if (criteriaScid) {
          return row.criteria_id === criteriaScid
        }
        return !target || normName(row.criteria_name) === target
      })
      .sort((rowA, rowB) => String(rowA.date).localeCompare(String(rowB.date)))
  }, [trend, criterionName, criteriaScid])

  const trendOption = useMemo(() => {
    return {
      tooltip: {
        trigger: 'axis',
        formatter: (params) => {
          const point = params[0]
          const row = trendRows[point.dataIndex] || {}
          return `${point.axisValue}<br/>Pass rate: ${point.value.toFixed(1)}%<br/>Passed: ${row.pass_count ?? 0}<br/>Failed: ${row.fail_count ?? 0}`
        },
      },
      grid: { left: 40, right: 16, top: 24, bottom: 32 },
      xAxis: {
        type: 'category',
        data: trendRows.map((row) => row.date),
        axisLabel: { fontSize: 10 },
      },
      yAxis: { type: 'value', min: 0, max: 100, name: 'Pass Rate %' },
      series: [
        {
          type: 'line',
          // pass_rate from BE is a fraction in [0, 1]; render as %.
          data: trendRows.map(
            (row) => Math.round((parseFloat(row.pass_rate) || 0) * 100 * 10) / 10
          ),
          smooth: true,
          itemStyle: { color: CHART.BRAND },
          areaStyle: { color: CHART.BRAND, opacity: 0.15 },
        },
      ],
    }
  }, [trendRows])

  // `personal_failure_rate` (share of this user's calls that failed), not
  // `failing_percent` (share-of-total-failures), which misleads on low volume.
  const byAgentRows = useMemo(() => {
    return (topInfractors || [])
      .map((infractor) => ({
        name:
          `${infractor.first_name || ''} ${infractor.last_name || ''}`.trim() ||
          `User ${infractor.user_id}`,
        // personal_failure_rate is already 0..100 -- don't multiply again.
        value: Math.round((parseFloat(infractor.personal_failure_rate) || 0) * 10) / 10,
        infractor,
      }))
      .filter((row) => row.value > 0)
      .sort((rowA, rowB) => rowB.value - rowA.value)
      .slice(0, 8)
  }, [topInfractors])

  const byAgentOption = useMemo(() => {
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        formatter: (params) => {
          const point = params[0]
          const infractor = byAgentRows[point.dataIndex]?.infractor || {}
          const cta = clickEntity ? '<br/><em>Click to narrow this drawer to this agent</em>' : ''
          return `${escapeHtml(point.name)}<br/>${point.value}% personal fail rate<br/>${infractor.failed_call_count || 0} failed / ${infractor.call_count || 0} total${cta}`
        },
      },
      grid: { left: 130, right: 24, top: 16, bottom: 16 },
      xAxis: { type: 'value', max: 100, name: '% personal fail rate' },
      yAxis: {
        type: 'category',
        data: byAgentRows.map((row) => row.name),
        inverse: true,
        axisLabel: { fontSize: 10 },
      },
      series: [
        {
          type: 'bar',
          data: byAgentRows.map((row) => ({
            value: row.value,
            infractor: row.infractor,
            name: row.name,
          })),
          itemStyle: { color: CHART.CRITICAL },
          cursor: clickEntity ? 'pointer' : 'default',
        },
      ],
    }
  }, [byAgentRows, clickEntity])

  // Clicking a bar re-opens THIS drawer narrowed via `agentContext`.
  const byAgentEvents = useMemo(
    () => ({
      click: (event) => {
        const infractor = event?.data?.infractor
        if (!infractor || !clickEntity || !criterion) {
          return
        }
        clickEntity({
          type: 'criterion',
          payload: {
            ...criterion,
            agentContext: {
              user_id: infractor.user_id,
              full_name: event?.data?.name,
            },
          },
        })
      },
    }),
    [clickEntity, criterion]
  )

  if (!criterion) {
    return null
  }

  return (
    <EntityDrawerShell
      title={getCriterionLabel(criterion)}
      typeLabel="Criterion"
      subtitle={
        <>
          {criterion.scorecard_config_name &&
            (scorecardSid && clickEntity ? (
              <span>
                {' in '}
                <Button
                  link
                  onClick={() =>
                    clickEntity({
                      type: 'scorecard',
                      payload: {
                        scorecard_config_sid: scorecardSid,
                        sid: scorecardSid,
                        name: criterion.scorecard_config_name,
                      },
                    })
                  }
                  tooltip="Open the scorecard"
                >
                  {criterion.scorecard_config_name}
                </Button>
              </span>
            ) : (
              <span>
                {' in '}
                <em>{criterion.scorecard_config_name}</em>
              </span>
            ))}
          {agentContext?.full_name && (
            <Pill small informative className="entity-drawer-filter-pill">
              Filtered to <strong>{agentContext.full_name}</strong>
            </Pill>
          )}
        </>
      }
      onClose={onClose}
    >
      <ChartCard
        title="Pass Rate Over Time"
        option={trendOption}
        disabled={!scorecardSid}
        disabledMessage="Couldn’t resolve the scorecard for this criterion. Trend unavailable."
        loading={trendLoading}
        error={trendError}
        isEmpty={isEmpty(trendRows)}
        errorMessage="Couldn’t load the trend for this criterion."
        emptyMessage="No trend data for this criterion in the current window."
      />

      {/* Skip when scoped to one agent -- a 1-row leaderboard is noise. */}
      {!agentContext && (
        <Card title="Top Agents Failing This">
          {isEmpty(byAgentRows) ? (
            <EmptyHint>No failing agents for this criterion in the current window.</EmptyHint>
          ) : (
            <EChart option={byAgentOption} events={byAgentEvents} style={{ height: '280px' }} />
          )}
        </Card>
      )}

      <Card title={criteriaScid ? 'Calls where this criterion failed' : 'Scored calls'}>
        <ScoredCallsBrowser
          scopeFilters={buildDrawerScopeFilters(filters, {
            scorecards: scorecardSid ? [{ value: scorecardSid }] : filters?.scorecards,
            agents: agentContext?.user_id ? [{ value: agentContext.user_id }] : filters?.agents,
            // Scope to this criterion's failures; the tier tabs then split
            // that set by overall score, so default to "all" not "below".
            failedCriteriaScid: criteriaScid || undefined,
          })}
          orgId={orgId}
          initialTier={criteriaScid ? 'all' : 'below'}
        />
      </Card>
    </EntityDrawerShell>
  )
}
